import { Injectable } from "@angular/core";
import { CONTENT_BLOCK_KINDS } from "../model/block.types";
import type {
  UnifiedBlock,
  NormalizedContentBlock,
  ContentBlockData,
  ApiEndpointData,
  ToolFrontendData,
  BackgroundJobBlockData,
  BlockStatus,
  PlanData,
} from "../model/block.types";

/**
 * Parses raw AG-UI state snapshot messages into typed NormalizedContentBlock[].
 * Reads only blocks with type === "non_standard" and a known value.kind.
 */
@Injectable({ providedIn: "root" })
export class ContentBlockParserService {
  /**
   * Extracts and normalizes all content blocks from an AG-UI state snapshot.
   * @param stateSnapshot - Raw state snapshot object from the AG-UI agent.
   * @returns Ordered array of NormalizedContentBlock, one per recognized non_standard block.
   */
  parseStateSnapshot(stateSnapshot: unknown): NormalizedContentBlock[] {
    const messages = this.#extractMessages(stateSnapshot);
    const blocks: NormalizedContentBlock[] = [];

    for (const message of messages) {
      const content = (message as { content?: unknown }).content;
      if (!Array.isArray(content)) continue;

      for (const item of content) {
        const block = this.parseBlock(item);
        if (block) blocks.push(block);
      }
    }

    return blocks;
  }

  /**
   * Extracts and normalizes all content blocks for a single message by messageId.
   */
  parseMessageBlocks(stateSnapshot: unknown, messageId: string): NormalizedContentBlock[] {
    const messages = this.#extractMessages(stateSnapshot);
    const message = messages.find((m) => (m as { id?: string }).id === messageId);
    if (!message) return [];

    const content = (message as { content?: unknown }).content;
    if (!Array.isArray(content)) return [];

    const blocks: NormalizedContentBlock[] = [];
    for (const item of content) {
      const block = this.parseBlock(item);
      if (block) blocks.push(block);
    }
    return blocks;
  }

  /**
   * Extracts and normalizes content blocks per message id across a whole state snapshot —
   * the stream-layer entry point for ContentBlockStreamService (chat-snapshot-stream-reducer
   * S3): the reducer reduces EVERY message of a snapshot, not one pre-known message id, and
   * the message id list lives only inside the snapshot itself. Reuses `#extractMessages` +
   * `parseBlock` (same parse pipeline as `parseStateSnapshot`/`parseMessageBlocks`).
   *
   * A message whose content parses to zero blocks still yields its (empty) entry, so the
   * caller can distinguish "message present, no blocks" from "message absent". The FIRST
   * message wins for a repeated id (same lookup order as `parseMessageBlocks`' `Array.find`);
   * duplicate block ids inside one message are NOT deduped here — that is the caller's
   * accumulation rule (last occurrence wins).
   * @param stateSnapshot - Raw state snapshot object from the AG-UI agent.
   * @returns Map of message id → ordered NormalizedContentBlock[] (empty when no messages).
   */
  parseBlocksByMessage(stateSnapshot: unknown): ReadonlyMap<string, NormalizedContentBlock[]> {
    const byMessage = new Map<string, NormalizedContentBlock[]>();
    for (const message of this.#extractMessages(stateSnapshot)) {
      const id = (message as { id?: unknown }).id;
      if (typeof id !== "string" || id.length === 0) continue;
      if (byMessage.has(id)) continue;

      const content = (message as { content?: unknown }).content;
      const blocks: NormalizedContentBlock[] = [];
      if (Array.isArray(content)) {
        for (const item of content) {
          const block = this.parseBlock(item);
          if (block) blocks.push(block);
        }
      }
      byMessage.set(id, blocks);
    }
    return byMessage;
  }

  /**
   * Extracts and normalizes all content blocks referenced by a `workspace_index_snapshot` state
   * key — same block shape (`mcp_tool`/`plan`) as any block the LLM/user would receive for that
   * evidence (content-blocks S5 §1.2). Entries whose kind has no block representation are
   * naturally dropped by parseBlock returning null.
   * @param stateSnapshot - Raw state snapshot object from the AG-UI agent.
   * @returns Ordered array of NormalizedContentBlock, one per recognized entry.
   */
  parseWorkspaceIndexSnapshot(stateSnapshot: unknown): NormalizedContentBlock[] {
    const snap = stateSnapshot as Record<string, unknown> | null | undefined;
    if (!snap) return [];
    const entries =
      snap.workspace_index_snapshot ?? (snap.values as Record<string, unknown> | undefined)?.workspace_index_snapshot;
    if (!Array.isArray(entries)) return [];

    const blocks = new Map<string, NormalizedContentBlock>();
    for (const entry of entries) {
      const block = this.parseBlock(entry);
      if (block) blocks.set(block.id, block);
    }
    // Dedupe by block id (NG0955): the snapshot can repeat an evidence id (e.g. the workarea
    // block re-appended on every turn that touched it) and the workspace index renders one
    // row per id. Map keeps the first-appearance position while the re-set value lets the
    // LAST occurrence win, so a repeated block shows its freshest status/data.
    return [...blocks.values()];
  }

  #extractMessages(stateSnapshot: unknown): unknown[] {
    const snap = stateSnapshot as Record<string, unknown> | null | undefined;
    if (!snap) return [];
    const msgs = snap.messages ?? (snap.values as Record<string, unknown> | undefined)?.messages;
    return Array.isArray(msgs) ? msgs : [];
  }

  /**
   * Normalizes a single raw `UnifiedBlock` envelope (`type: "non_standard"`) into a typed
   * NormalizedContentBlock, or null when the shape/kind is not recognized.
   * @param item - Raw content block item, as received inside a message's `content` array or a
   * `workspace_index_snapshot` entry.
   */
  parseBlock(item: unknown): NormalizedContentBlock | null {
    if (!item || typeof item !== "object") return null;

    const block = item as UnifiedBlock;

    if (block.type !== "non_standard") return null;
    if (!block.value?.kind || !block.value?.id) return null;

    const { kind, id, status, data } = block.value;
    const safeStatus = this.#normalizeStatus(status);

    if (CONTENT_BLOCK_KINDS.has(kind)) {
      return { kind, id, status: safeStatus, data: data as ContentBlockData } as NormalizedContentBlock;
    }

    switch (kind) {
      case "api_endpoint":
        return { kind: "api_endpoint", id, status: safeStatus, data: data as ApiEndpointData };

      case "tool_frontend":
        return { kind: "tool_frontend", id, status: safeStatus, data: data as ToolFrontendData };

      case "background_job":
        return { kind: "background_job", id, status: safeStatus, data: data as BackgroundJobBlockData };

      case "plan":
        return { kind: "plan", id, status: safeStatus, data: data as PlanData };

      // Dedicated card (map-layer-block-ui S3): map_layer rides the ContentBlockData arm via
      // this explicit case — it is deliberately NOT a CONTENT_BLOCK_KINDS member (that set
      // means "shares the single generic card").
      case "map_layer":
        return { kind: "map_layer", id, status: safeStatus, data: data as ContentBlockData };

      default:
        console.error("ContentBlockParserService: unknown block kind:", kind, block);
        return null;
    }
  }

  #normalizeStatus(status: unknown): BlockStatus {
    if (status === "pending" || status === "completed" || status === "error") {
      return status;
    }
    return "pending";
  }
}
